import React, { useState, useMemo } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonSkeletonText,
  IonMenuButton,
  IonButtons,
  IonIcon,
  IonRefresher,
  IonRefresherContent,
  IonFab,
  IonFabButton,
  useIonRouter,
  useIonToast,
} from '@ionic/react';
import {
  search,
  listOutline,
  gridOutline,
  chevronBack,
  chevronForward,
  people,
  musicalNotes,
  school,
  shield,
  layers,
  add,
  close,
  createOutline,
  trashOutline,
} from 'ionicons/icons';
import { useGroups, useCreateGroup, useUpdateGroup, useDeleteGroup } from '@/hooks/useGroups';
import { usePermission } from '@/hooks/usePermission';
import { GroupCategory } from '@/types/group';
import type { Group, GroupFormData } from '@/types/group';
import { GROUP_CATEGORY_LABELS, GROUP_CATEGORY_COLORS } from '@/utils/labels';
import ConfirmDialog from '@/components/shared/ConfirmDialog';

const CATEGORY_LABELS = GROUP_CATEGORY_LABELS;

const CATEGORY_ICONS: Record<string, string> = {
  [GroupCategory.SECTOR]: layers,
  [GroupCategory.TINERI]: people,
  [GroupCategory.MUZICAL]: musicalNotes,
  [GroupCategory.SBC]: school,
  [GroupCategory.ALTELE]: shield,
};

const CATEGORY_COLORS = GROUP_CATEGORY_COLORS;

type ViewMode = 'list' | 'grid';
type SortKey = 'name' | 'member_count' | 'category';

const EMPTY_FORM: GroupFormData = { name: '', description: '', category: GroupCategory.ALTELE };

const GroupListPage: React.FC = () => {
  const router = useIonRouter();
  const { data: groupsData, isLoading, refetch } = useGroups({ per_page: 100 });
  const { can } = usePermission();
  const canCreate = can('groups.create');
  const canEdit = can('groups.edit');
  const canDelete = can('groups.delete');
  const [presentToast] = useIonToast();

  const createMut = useCreateGroup();
  const updateMut = useUpdateGroup();
  const deleteMut = useDeleteGroup();

  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [sortKey, setSortKey] = useState<SortKey>('category');
  const [sortAsc, setSortAsc] = useState(true);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 30;
  const [showSearch, setShowSearch] = useState(false);
  const [searchText, setSearchText] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [formData, setFormData] = useState<GroupFormData>(EMPTY_FORM);
  const [deleteGroupId, setDeleteGroupId] = useState<string | null>(null);

  const groups = groupsData?.data ?? [];

  const handleRefresh = async (event: CustomEvent) => {
    await refetch();
    event.detail.complete();
  };

  const filteredGroups = useMemo(() => {
    let result = [...groups];
    if (activeCategory) {
      result = result.filter((g) => g.category === activeCategory);
    }
    if (searchText) {
      const q = searchText.toLowerCase();
      result = result.filter((g) =>
        g.name.toLowerCase().includes(q) ||
        (g.description ?? '').toLowerCase().includes(q),
      );
    }
    result.sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'name') cmp = (a.name ?? '').localeCompare(b.name ?? '');
      else if (sortKey === 'member_count') cmp = (a.member_count ?? 0) - (b.member_count ?? 0);
      else cmp = (a.category ?? '').localeCompare(b.category ?? '');
      return sortAsc ? cmp : -cmp;
    });
    return result;
  }, [groups, activeCategory, searchText, sortKey, sortAsc]);

  const totalPages = Math.ceil(filteredGroups.length / pageSize);
  const pagedGroups = filteredGroups.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(true); }
  };

  const categoryCounts = useMemo(() => {
    return groups.reduce((acc: Record<string, number>, g) => {
      const cat = g.category || GroupCategory.ALTELE;
      acc[cat] = (acc[cat] || 0) + 1;
      return acc;
    }, {});
  }, [groups]);

  const sortArrow = (key: SortKey) => (sortKey === key ? (sortAsc ? ' ↑' : ' ↓') : '');

  const navigateToGroup = (id: string) => router.push(`/groups/${id}`);

  const deleteGroupName = deleteGroupId ? groups.find((g) => g.id === deleteGroupId)?.name : '';

  // ── Modal handlers ──
  const openCreate = () => {
    setEditingGroup(null);
    setFormData(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (group: Group, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingGroup(group);
    setFormData({ name: group.name, description: group.description ?? '', category: group.category });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      void presentToast({ message: 'Numele grupului este obligatoriu.', duration: 2000, color: 'warning', position: 'bottom' });
      return;
    }
    try {
      if (editingGroup) {
        await updateMut.mutateAsync({ id: editingGroup.id, data: formData });
        void presentToast({ message: 'Grup actualizat.', duration: 2000, color: 'success', position: 'bottom' });
      } else {
        await createMut.mutateAsync(formData);
        void presentToast({ message: 'Grup creat.', duration: 2000, color: 'success', position: 'bottom' });
      }
      setShowModal(false);
    } catch {
      void presentToast({ message: 'Eroare la salvare.', duration: 2000, color: 'danger', position: 'bottom' });
    }
  };

  const handleDelete = async () => {
    if (!deleteGroupId) return;
    try {
      await deleteMut.mutateAsync(deleteGroupId);
      void presentToast({ message: 'Grup șters.', duration: 2000, color: 'success', position: 'bottom' });
    } catch {
      void presentToast({ message: 'Eroare la ștergere.', duration: 2000, color: 'danger', position: 'bottom' });
    }
    setDeleteGroupId(null);
  };

  const isSaving = createMut.isPending || updateMut.isPending;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar className="crm-toolbar">
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Grupuri</IonTitle>
          <IonButtons slot="end">
            <button className="crm-toolbar-icon" onClick={() => { setShowSearch(!showSearch); if (showSearch) setSearchText(''); }}>
              <IonIcon icon={showSearch ? close : search} />
            </button>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        {showSearch && (
          <div style={{ padding: '12px 20px 0', maxWidth: 1200, margin: '0 auto' }}>
            <input
              type="text"
              placeholder="Caută grupuri..."
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); setCurrentPage(1); }}
              autoFocus
              style={{
                width: '100%', padding: '10px 0', fontSize: 14, fontWeight: 700,
                fontFamily: "'Lato', sans-serif", color: '#1c1d21',
                border: 'none', borderBottom: '1px solid #ececf2', outline: 'none',
                background: 'transparent',
              }}
              onFocus={(e) => { e.target.style.borderBottomColor = '#5e81f4'; }}
              onBlur={(e) => { e.target.style.borderBottomColor = '#ececf2'; }}
            />
          </div>
        )}

        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '16px 20px' }}>

          {/* ── Category filter bar (horizontal) ── */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
            <button
              onClick={() => { setActiveCategory(null); setCurrentPage(1); }}
              className={`form-chip${!activeCategory ? ' form-chip--active' : ''}`}
              style={{ fontSize: 13 }}
            >
              Toate ({groups.length})
            </button>
            {Object.values(GroupCategory).map((cat) => {
              const count = categoryCounts[cat] || 0;
              if (count === 0) return null;
              return (
                <button
                  key={cat}
                  onClick={() => { setActiveCategory(activeCategory === cat ? null : cat); setCurrentPage(1); }}
                  className={`form-chip${activeCategory === cat ? ' form-chip--active' : ''}`}
                  style={{
                    fontSize: 13,
                    borderColor: activeCategory === cat ? CATEGORY_COLORS[cat] : undefined,
                    color: activeCategory === cat ? CATEGORY_COLORS[cat] : undefined,
                  }}
                >
                  <IonIcon icon={CATEGORY_ICONS[cat] ?? shield} style={{ fontSize: 13, marginRight: 4 }} />
                  {CATEGORY_LABELS[cat]} ({count})
                </button>
              );
            })}
          </div>

          {/* ── Controls bar ── */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div className="crm-view-toggle">
              <button
                className={`crm-view-toggle__btn ${viewMode === 'grid' ? 'crm-view-toggle__btn--active' : ''}`}
                onClick={() => setViewMode('grid')}
              >
                <IonIcon icon={gridOutline} style={{ fontSize: 14 }} /> Grilă
              </button>
              <button
                className={`crm-view-toggle__btn ${viewMode === 'list' ? 'crm-view-toggle__btn--active' : ''}`}
                onClick={() => setViewMode('list')}
              >
                <IonIcon icon={listOutline} style={{ fontSize: 14 }} /> Listă
              </button>
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <select
                className="form-input form-select"
                value={sortKey}
                onChange={(e) => { setSortKey(e.target.value as SortKey); setCurrentPage(1); }}
                style={{ height: 32, fontSize: 12, fontWeight: 600, padding: '0 28px 0 8px', minWidth: 0, width: 'auto', textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                <option value="name">Nume</option>
                <option value="member_count">Membri</option>
                <option value="category">Categorie</option>
              </select>
              <button className="crm-sort-button" onClick={() => setSortAsc(!sortAsc)} style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                {sortAsc ? '↑' : '↓'}
              </button>
            </div>
          </div>

          {/* ── Content ── */}
          {isLoading ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="crm-card" style={{ padding: 16 }}>
                  <IonSkeletonText animated style={{ width: '60%', height: 16 }} />
                  <IonSkeletonText animated style={{ width: '40%', height: 12, marginTop: 8 }} />
                  <IonSkeletonText animated style={{ width: '30%', height: 12, marginTop: 6 }} />
                </div>
              ))}
            </div>
          ) : viewMode === 'grid' ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
              {pagedGroups.map((group) => {
                const catColor = CATEGORY_COLORS[group.category] || '#64748b';
                return (
                  <div
                    key={group.id}
                    className="crm-card crm-animate-in"
                    style={{ cursor: 'pointer', padding: '16px 16px 14px', position: 'relative', transition: 'box-shadow 0.15s' }}
                    onClick={() => navigateToGroup(group.id)}
                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ''; }}
                  >
                    {/* Edit/Delete on hover */}
                    {(canEdit || canDelete) && (
                      <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 3, opacity: 0.6, transition: 'opacity 0.15s' }}
                        onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.6'; }}
                      >
                        {canEdit && (
                          <button
                            onClick={(e) => openEdit(group, e)}
                            style={{ width: 26, height: 26, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4f46e5', background: '#eef2ff', border: 'none', cursor: 'pointer', fontSize: 12 }}
                          >
                            <IonIcon icon={createOutline} />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeleteGroupId(group.id); }}
                            style={{ width: 26, height: 26, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#dc2626', background: '#fef2f2', border: 'none', cursor: 'pointer', fontSize: 12 }}
                          >
                            <IonIcon icon={trashOutline} />
                          </button>
                        )}
                      </div>
                    )}

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, paddingRight: 56 }}>
                      <div
                        className="crm-table__icon"
                        style={{ backgroundColor: catColor, width: 36, height: 36, borderRadius: 9, flexShrink: 0 }}
                      >
                        <IonIcon icon={CATEGORY_ICONS[group.category] ?? shield} style={{ fontSize: 16 }} />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{group.name}</div>
                        <span className="crm-badge" style={{ backgroundColor: `${catColor}12`, color: catColor, fontSize: 10, padding: '1px 6px' }}>
                          {CATEGORY_LABELS[group.category] ?? group.category}
                        </span>
                      </div>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>
                      {group.member_count ?? group.members_count ?? 0} <span style={{ fontWeight: 400, color: '#94a3b8' }}>membri</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="crm-card" style={{ overflow: 'auto' }}>
              <table className="crm-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('name')} style={{ minWidth: 200, cursor: 'pointer' }}>
                      Grup{sortArrow('name')}
                    </th>
                    <th onClick={() => handleSort('member_count')} style={{ width: 100, cursor: 'pointer' }}>
                      Membri{sortArrow('member_count')}
                    </th>
                    <th onClick={() => handleSort('category')} style={{ width: 140, cursor: 'pointer' }}>
                      Categorie{sortArrow('category')}
                    </th>
                    <th style={{ width: 80 }}>Acțiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedGroups.map((group) => {
                    const catColor = CATEGORY_COLORS[group.category] || '#64748b';
                    return (
                      <tr
                        key={group.id}
                        onClick={() => navigateToGroup(group.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div className="crm-table__icon" style={{ backgroundColor: catColor }}>
                              <IonIcon icon={CATEGORY_ICONS[group.category] ?? shield} />
                            </div>
                            <div>
                              <div className="crm-table__name">{group.name}</div>
                              {group.description && (
                                <div className="crm-table__sub">{group.description}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span style={{ fontWeight: 600 }}>{group.member_count ?? group.members_count ?? 0}</span>
                          <span style={{ color: '#94a3b8', fontSize: 12 }}> membri</span>
                        </td>
                        <td>
                          <span className="crm-badge" style={{
                            backgroundColor: `${catColor}12`,
                            color: catColor,
                          }}>
                            {CATEGORY_LABELS[group.category] ?? group.category}
                          </span>
                        </td>
                        <td>
                          <div className="crm-table__actions">
                            {canEdit && (
                              <button
                                className="crm-sort-button"
                                onClick={(e) => openEdit(group, e)}
                                title="Editează"
                                style={{ width: 30, height: 30, borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#4f46e5', background: '#eef2ff' }}
                              >
                                <IonIcon icon={createOutline} style={{ fontSize: 14 }} />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                className="crm-sort-button"
                                onClick={(e) => { e.stopPropagation(); setDeleteGroupId(group.id); }}
                                title="Șterge"
                                style={{ width: 30, height: 30, borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#dc2626', background: '#fef2f2' }}
                              >
                                <IonIcon icon={trashOutline} style={{ fontSize: 14 }} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Pagination ── */}
          {!isLoading && totalPages > 1 && (
            <div className="crm-pagination" style={{ marginTop: 16 }}>
              <button
                className="crm-pagination__btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                <IonIcon icon={chevronBack} style={{ fontSize: 12 }} /> PREV
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  className={`crm-pagination__num ${p === currentPage ? 'crm-pagination__num--active' : ''}`}
                  onClick={() => setCurrentPage(p)}
                >
                  {p}
                </button>
              ))}
              <button
                className="crm-pagination__btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                NEXT <IonIcon icon={chevronForward} style={{ fontSize: 12 }} />
              </button>
            </div>
          )}

          {!isLoading && filteredGroups.length === 0 && (
            <div className="crm-empty-state">
              <div className="crm-empty-state__icon"><IonIcon icon={people} /></div>
              <h3 className="crm-empty-state__title">Niciun grup găsit</h3>
              <p className="crm-empty-state__text">
                {activeCategory ? 'Nu există grupuri în această categorie.' : searchText ? 'Nicio potrivire pentru căutare.' : 'Nu există grupuri înregistrate.'}
              </p>
            </div>
          )}
        </div>

        {/* ── FAB: Create Group ── */}
        {canCreate && (
          <IonFab vertical="bottom" horizontal="end" slot="fixed">
            <IonFabButton onClick={openCreate}>
              <IonIcon icon={add} />
            </IonFabButton>
          </IonFab>
        )}

        {/* ── Create/Edit Group Modal ── */}
        {showModal && (
          <div className="logout-overlay" onClick={() => { if (!isSaving) setShowModal(false); }}>
            <div className="logout-dialog" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440, width: '90%', textAlign: 'left' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                  {editingGroup ? 'Editare Grup' : 'Grup Nou'}
                </h3>
                <button onClick={() => setShowModal(false)} disabled={isSaving} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                  <IonIcon icon={close} style={{ fontSize: 22, color: '#94a3b8' }} />
                </button>
              </div>

              {/* Name */}
              <div className="form-field" style={{ marginBottom: 16 }}>
                <label className="form-field__label">Nume grup *</label>
                <input
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="ex: Cor Mixt"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter') { void handleSave(); } }}
                />
              </div>

              {/* Category */}
              <div className="form-field" style={{ marginBottom: 16 }}>
                <label className="form-field__label">Categorie</label>
                <select
                  className="form-input"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value as GroupCategory })}
                >
                  {Object.values(GroupCategory).map((cat) => (
                    <option key={cat} value={cat}>{CATEGORY_LABELS[cat] ?? cat}</option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div className="form-field" style={{ marginBottom: 24 }}>
                <label className="form-field__label">Descriere</label>
                <textarea
                  className="form-input"
                  value={formData.description ?? ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descriere opțională..."
                  rows={3}
                  style={{ resize: 'vertical' }}
                />
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button className="form-btn form-btn--secondary" onClick={() => setShowModal(false)} disabled={isSaving}>
                  Anulează
                </button>
                <button className="form-btn form-btn--primary" onClick={handleSave} disabled={isSaving}>
                  {isSaving && <span className="login-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />}
                  {isSaving ? 'Se salvează...' : editingGroup ? 'Salvează' : 'Creează'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Delete Confirmation ── */}
        <ConfirmDialog
          isOpen={!!deleteGroupId}
          title="Ștergere Grup"
          message={`Sigur doriți să ștergeți grupul "${deleteGroupName}"? Membrii vor fi dezasociați, nu șterși.`}
          confirmLabel="Șterge"
          variant="danger"
          isLoading={deleteMut.isPending}
          onConfirm={handleDelete}
          onCancel={() => setDeleteGroupId(null)}
        />
      </IonContent>
    </IonPage>
  );
};

export default GroupListPage;
